import React, { useState } from "react";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#00CCC9", "#6366F1", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#0EA5E9", "#84CC16", "#F97316", "#64748B"];

function SubActivityRow({ cat, sub, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sub.name);

  const save = async () => {
    try {
      await api.put(`/kpi/activity-categories/${cat.id}/sub-activities/${sub.id}`, { name });
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/kpi/activity-categories/${cat.id}/sub-activities/${sub.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (sub.id === "custom") return null;

  return (
    <div className="flex items-center gap-2 pl-4 py-1" data-testid={`subactivity-row-${sub.id}`}>
      <span className="text-slate-400">–</span>
      {editing ? (
        <>
          <input className="input-tech text-xs flex-1" value={name} onChange={(e) => setName(e.target.value)} data-testid={`subactivity-edit-input-${sub.id}`} />
          <button onClick={save} data-testid={`subactivity-save-${sub.id}`}><Check size={13} className="text-emerald-600" /></button>
          <button onClick={() => setEditing(false)}><X size={13} className="text-slate-400" /></button>
        </>
      ) : (
        <>
          <span className="text-xs text-slate-700 flex-1">{sub.name}</span>
          <button onClick={() => setEditing(true)} data-testid={`subactivity-edit-${sub.id}`}><Pencil size={12} className="text-slate-400 hover:text-slate-800" /></button>
          <button onClick={remove} data-testid={`subactivity-delete-${sub.id}`}><Trash2 size={12} className="text-slate-400 hover:text-red-600" /></button>
        </>
      )}
    </div>
  );
}

function CategoryRow({ cat, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [newSub, setNewSub] = useState("");

  const save = async () => {
    try {
      await api.put(`/kpi/activity-categories/${cat.id}`, { name });
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const remove = async () => {
    if (!window.confirm(`"${cat.name}" wirklich löschen? Bereits erfasste Zeiteinträge bleiben erhalten.`)) return;
    try {
      await api.delete(`/kpi/activity-categories/${cat.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const addSub = async () => {
    if (!newSub.trim()) return;
    try {
      await api.post(`/kpi/activity-categories/${cat.id}/sub-activities`, { name: newSub.trim() });
      setNewSub("");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="border border-slate-200 p-3" data-testid={`category-row-${cat.id}`}>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: cat.color }} />
        {editing ? (
          <>
            <input className="input-tech text-sm flex-1" value={name} onChange={(e) => setName(e.target.value)} data-testid={`category-edit-input-${cat.id}`} />
            <button onClick={save} data-testid={`category-save-${cat.id}`}><Check size={14} className="text-emerald-600" /></button>
            <button onClick={() => setEditing(false)}><X size={14} className="text-slate-400" /></button>
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-slate-900 flex-1">{cat.name}</span>
            <button onClick={() => setEditing(true)} data-testid={`category-edit-${cat.id}`}><Pencil size={13} className="text-slate-400 hover:text-slate-800" /></button>
            <button onClick={remove} data-testid={`category-delete-${cat.id}`}><Trash2 size={13} className="text-slate-400 hover:text-red-600" /></button>
          </>
        )}
      </div>
      {(cat.sub_activities || []).length > 0 && (
        <div className="mt-1.5">
          {cat.sub_activities.map((s) => <SubActivityRow key={s.id} cat={cat} sub={s} onChanged={onChanged} />)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 pl-4">
        <input className="input-tech text-xs flex-1" placeholder="Neue Unteraufgabe …" value={newSub}
          onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()}
          data-testid={`new-subactivity-input-${cat.id}`} />
        <button onClick={addSub} data-testid={`new-subactivity-add-${cat.id}`}
          className="text-xs font-medium text-[#00807E] hover:underline flex items-center gap-1 whitespace-nowrap">
          <Plus size={12} /> Aufgabe
        </button>
      </div>
    </div>
  );
}

export default function ActivityCategoryManager({ open, onOpenChange, categories, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLORS[0]);

  const addCategory = async () => {
    if (!newName.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    try {
      await api.post("/kpi/activity-categories", { name: newName.trim(), color: newColor });
      setNewName("");
      onChanged();
      toast.success("Tätigkeit angelegt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="activity-category-manager">
        <DialogHeader>
          <DialogTitle>Tätigkeiten verwalten</DialogTitle>
        </DialogHeader>
        <div className="border border-dashed border-slate-300 p-3 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Neue Tätigkeit anlegen</div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setNewColor(c)} data-testid={`new-category-color-${c}`}
                  className={`w-5 h-5 rounded-sm border-2 ${newColor === c ? "border-slate-900" : "border-transparent"}`}
                  style={{ background: c }} />
              ))}
            </div>
            <input className="input-tech text-sm flex-1" placeholder="z. B. Dokumentationszeit" value={newName}
              onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()}
              data-testid="new-category-name-input" />
            <button onClick={addCategory} data-testid="new-category-add"
              className="px-3 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-black flex items-center gap-1 whitespace-nowrap">
              <Plus size={13} /> Anlegen
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {categories.map((c) => <CategoryRow key={c.id} cat={c} onChanged={onChanged} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
